import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { ListDocumentsRequest } from "protogen/documents_service_pb";
import { Document, DocumentType } from "protogen/documents_pb";
import FlexPage from "components/layout/FlexPage";
import useIsMobile from "components/hooks/useIsMobile";
import {
  useCreateMemberDocument,
  useListMemberDocuments,
} from "services/documents";
import { useGetMemberDetails } from "services/member";
import SearchBar from "components/forum/SearchBar";
import DocumentsTable from "components/documents/DocumentsTable";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "components/common/ConfirmationDialog";
import Loading from "components/common/Loading";
import Breadcrumbs from "components/common/Breadcrumbs";

export default () => {
  const params = useParams();
  const isMobile = useIsMobile();
  const [allDocuments, setAllDocuments] = useState<Document[]>([]);
  const [filteredAndSortedDocuments, setFilteredAndSortedDocuments] = useState<
    Document[] | null
  >(null);
  const [query, setQuery] = useState<string>("");
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const { request: listDocumentsRequest } = useListMemberDocuments();
  useGetMemberDetails();
  const confirmState = useConfirmationDialog();
  const { request: createDocumentRequest } = useCreateMemberDocument();
  const navigate = useNavigate();

  const createDocument = async () => {
    const result = await createDocumentRequest();
    navigate(`/documents/${encodeURIComponent(result?.document?.uuid || "")}`);
  };

  const onLoad = async () => {
    listDocuments();
  };

  const listDocuments = async () => {
    const topics = params.topics
      ? decodeURIComponent(params.topics).split(",")
      : [];

    const r = await listDocumentsRequest(
      new ListDocumentsRequest({
        documentTypes: [DocumentType.NOTE],
        favoritesOnly: false,
        includeCommunitySharedDocuments: false,
        anyTopics: topics,
      }),
    );
    let documents = r?.documents || [];
    setAllDocuments(documents);
    setHasLoadedOnce(true);
  };

  const filterDocuments = (documents: Document[], searchQuery: string) => {
    if (!searchQuery.trim()) {
      return documents;
    }

    const lowercaseQuery = searchQuery.toLowerCase();
    return documents.filter((doc) => {
      return (
        doc.title?.toLowerCase().includes(lowercaseQuery) ||
        doc.topics?.some((topic) =>
          topic.toLowerCase().includes(lowercaseQuery),
        )
      );
    });
  };

  useEffect(() => {
    if (!hasLoadedOnce) return;
    const filtered = filterDocuments(allDocuments, query);
    setFilteredAndSortedDocuments(filtered);
  }, [allDocuments, query, hasLoadedOnce]);

  useEffect(() => {
    setFilteredAndSortedDocuments(null);
    onLoad();
  }, []);

  return (
    <FlexPage leftAligned sx={{ width: "100%" }}>
      <Breadcrumbs
        breadcrumbs={[{ name: "Home", link: "/" }, { name: "Documents" }]}
      />
      <Typography variant="display" sx={{ marginBottom: "24px" }}>
        Documents
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "32px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
            justifyContent: "space-between",
          }}
        >
          <SearchBar
            fullWidth={isMobile}
            onQuery={(query) => {
              setQuery(query);
            }}
            onKeyPress={true}
            sx={{ marginLeft: "0px" }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              createDocument();
            }}
          >
            {isMobile ? "Add" : "New document"}
          </Button>
        </Box>

        {!filteredAndSortedDocuments ? (
          <Loading />
        ) : (
          <DocumentsTable
            documents={filteredAndSortedDocuments}
            layout="simple"
            showFavoriteCount={false}
          />
        )}
      </Box>

      <ConfirmationDialog
        content={"Are you sure you want to remove this document?"}
        {...confirmState.dialogProps}
      />
    </FlexPage>
  );
};
